import React from "react";
import { useTranslation } from "react-i18next";
import {
    ButtonBase,
    ButtonIconTrash,
    InputAutoresize,
    InputBase,
    InputCheckbox,
    InputDate,
    InputFile,
    InputSelect,
} from "../../../common/components/form";
import InputMenuLinkTitle from "./InputMenuLinkTitle.jsx";

const ACTIONS_MAX = 3;

const FileInputsByProductType = {
    "mobile-app": [
        {
            placeholder: "WizardWStep4MobileSecondaryPictureText",
            index: 0,
            sizes: ["412x890", "824x1780"],
        },
        {
            placeholder: "WizardWStep4MobileMainPictureText",
            index: 1,
            sizes: ["412x890", "824x1780"],
        },
    ],

    "web-product": [
        {
            placeholder: "WizardWStep4WebProductPictureText",
            index: 0,
            sizes: ["888x556", "1776x1112"],
        },
    ],

    "cross-platform": [
        {
            placeholder: "WizardWStep4TabletPictureText",
            index: 0,
            sizes: ["540x376", "1080x752"],
        },
        {
            placeholder: "WizardWStep4DesktopPictureText",
            index: 1,
            sizes: ["364x228", "728x456"],
        },
        {
            placeholder: "WizardWStep4MobilePictureText",
            index: 2,
            sizes: ["164x356", "328x712"],
        },
    ],

    ecommerce: [
        {
            placeholder: "WizardWStep4eCommercePrimaryText",
            index: 0,
        },
    ],

    services: [
        {
            placeholder: "WizardWStep4AdventurePrimaryText",
            index: 0,
            sizes: ["1600x900", "3200x1800"],
            overlayHidding: true,
        },
    ],

    event: [
        {
            placeholder: "WizardWStep4AdventurePrimaryText",
            index: 0,
            sizes: ["1600x900", "3200x1800"],
            overlayHidding: true,
        },
    ],

    edutainment: [
        {
            placeholder: "WizardWStep4AdventurePrimaryText",
            index: 0,
            sizes: ["1600x900", "3200x1800"],
            overlayHidding: true,
        },
    ],
};

export default function WStep4Focus(props) {
    const { t } = useTranslation();

    const { set, isCustom, variables, variables2block } = props;

    const items = isCustom ? [variables2block] : [{ variables }];

    return (
        <div className="wizard-part-inner">
            <div style={{ flex: 1 }}>
                <h1>{t("WizardWStep4Header")}</h1>

                {items.map((item, index) => (
                    <Item key={index} blockIndex={index} {...item} set={(...args) => set(...args, item)} />
                ))}
            </div>
        </div>
    );
}

function Item(props) {
    const { t } = useTranslation();

    const {
        set,
        variables: {
            Actions,
            EventHeader,
            Events,
            ProductPicture,
            ProductPictureHideOverlayGradient,
            ProductType,
            Stimulus,
        },
    } = props;

    const isECommerce = ProductType === "ecommerce";

    const _Actions = Actions ?? [{}];
    const _Events = Events ?? [{}];
    const _ProductPicture = ProductPicture ?? new Array(FileInputsByProductType[ProductType]?.length).fill(null);

    function onChange({ target: { name, value } }) {
        set({ [name]: value });
    }

    function onEventItemChange({ name, value }, item) {
        for (const _item of _Events) {
            if (_item === item) {
                _item[name] = value;
                break;
            }
        }

        set({ Events: _Events });
    }

    function onActionItemChange({ name, value }, item) {
        for (const _item of _Actions) {
            if (_item === item) {
                _item[name] = value;
                break;
            }
        }

        set({ Actions: _Actions });
    }

    function onActionItemTypeSelect(type, item) {
        for (const _item of _Actions) {
            if (_item === item) {
                if (_item.type === type) {
                    break;
                }

                if (type === "offers") {
                    _item._externalLink_ = _item.link || "";
                    _item.link = "#Offers";
                } else if (type === "external-link") {
                    _item.link = _item?._externalLink_ || "";
                }

                _item.type = type;

                break;
            }
        }

        set({ Actions: _Actions });
    }

    function onDeleteItemClick(item) {
        set({ Actions: _Actions.filter(a => a !== item) });
    }

    function onAddItemClick() {
        _Actions.push({});
        set({ Actions: _Actions });
    }

    function onPictureSelect(file, { index, sizes }) {
        _ProductPicture[index] = { file, sizes };
        set({ ProductPicture: _ProductPicture });
    }

    const CNInputFiles = ["wizard-input-file-container"];
    ProductType === "mobile-app" && CNInputFiles.push("reverse");

    const ProductPictureExists = _ProductPicture.filter(pp => !!pp).length > 0;
    const CanHideOverlay = (FileInputsByProductType[ProductType] || []).some(({ overlayHidding }) => !!overlayHidding);

    return (
        <>
            <div className="margin-top-40">
                <InputMenuLinkTitle name="Home" {...props} />
            </div>

            <div className="margin-top-40">
                <p>{t("WizardWStep4StimulusText")}</p>

                <InputAutoresize
                    className="margin-top-20"
                    name="Stimulus"
                    onChange={onChange}
                    value={Stimulus || ""}
                    placeholder={t("WizardWStep4StimulusInputPlaceholder")}
                    rows="3"
                />
            </div>

            {EventHeader && (
                <div className="margin-top-40">
                    <p>{t("WizardWStep4EventText")}</p>

                    <InputBase
                        className="margin-top-20"
                        name="Location"
                        value={_Events[0].Location || ""}
                        onChange={({ target: { name, value } }) => onEventItemChange({ name, value }, _Events[0])}
                        placeholder={t("WizardWStep4EventText")}
                    />

                    <div className="flex-gap-20 grid-center">
                        <InputDate
                            className="margin-top-20"
                            type="date"
                            name="dateStart"
                            onChange={e => onEventItemChange(e, _Events[0])}
                            value={_Events[0].dateStart || ""}
                        />

                        <InputDate
                            className="margin-top-20"
                            type="date"
                            name="dateEnd"
                            onChange={e => onEventItemChange(e, _Events[0])}
                            value={_Events[0].dateEnd || ""}
                        />
                    </div>

                    <div className="flex-gap-20 grid-center">
                        <InputDate
                            className="margin-top-20"
                            type="time"
                            name="timeStart"
                            onChange={e => onEventItemChange(e, _Events[0])}
                            value={_Events[0].timeStart || ""}
                        />

                        <InputDate
                            className="margin-top-20"
                            type="time"
                            name="timeEnd"
                            onChange={e => onEventItemChange(e, _Events[0])}
                            value={_Events[0].timeEnd || ""}
                        />
                    </div>
                </div>
            )}

            <div className="margin-top-40 wizard-hero">
                <p>{t("WizardWStep4ActionsText")}</p>

                {_Actions.map((itemAction, index) => {
                    const isECommerceCheckout = isECommerce && itemAction?.attrs?.name === "CHECKOUT";

                    let InnerClassName = ["grid-gap-20"];
                    let InnerStyle = { gridTemplateColumns: "1fr" };
                    !isECommerceCheckout && InnerClassName.push("grid-two-column");
                    !isECommerceCheckout && (InnerStyle.gridTemplateColumns = "1fr 1fr");
                    InnerClassName = InnerClassName.join(" ");

                    return (
                        <div key={index} className="grid-gap-20 margin-top-20 wizard-input-actions">
                            <div className={InnerClassName} style={InnerStyle}>
                                <InputAutoresize
                                    className="full-width"
                                    name="text"
                                    onChange={({ target: { name, value } }) =>
                                        onActionItemChange({ name, value }, itemAction)
                                    }
                                    value={itemAction.text || ""}
                                    placeholder={t("WizardWStep4ActionTextInputPlaceholder")}
                                    disabled={isECommerceCheckout}
                                />

                                {!isECommerceCheckout && (
                                    <InputSelect
                                        fullWidth
                                        onSelect={value => onActionItemTypeSelect(value, itemAction)}
                                        selected={itemAction.type || "external-link"}
                                        options={[
                                            { value: "offers", text: t("ActionTypeOffers") },
                                            { value: "external-link", text: t("ActionTypeExternalLink") },
                                        ]}
                                        disabled={isECommerceCheckout}
                                    />
                                )}
                            </div>

                            <div>
                                <InputAutoresize
                                    className="full-width"
                                    name="link"
                                    onChange={({ target: { name, value } }) =>
                                        onActionItemChange({ name, value }, itemAction)
                                    }
                                    value={itemAction.link || ""}
                                    placeholder={t("WizardWStep4ActionLinkInputPlaceholder")}
                                    disabled={itemAction.type === "offers" || isECommerceCheckout}
                                />
                            </div>

                            <div className="absolute" style={{ left: "calc(100% + 20px)" }}>
                                <ButtonIconTrash
                                    disabled={_Actions.length < 2 || isECommerceCheckout}
                                    onClick={() => onDeleteItemClick(itemAction)}
                                />
                            </div>
                        </div>
                    );
                })}

                <div className="margin-top-20">
                    <ButtonBase dashed onClick={onAddItemClick} disabled={_Actions.length >= ACTIONS_MAX}>
                        {t("PlusAdd")}
                    </ButtonBase>
                </div>
            </div>

            {!isECommerce && (
                <div className={CNInputFiles.join(" ")}>
                    {(FileInputsByProductType[ProductType] || []).map(input => (
                        <div key={input.index} className="margin-top-40">
                            <p>{t(input.placeholder)}</p>

                            <div className="flex-center flex-gap-40 margin-top-20 wizard-input-file">
                                <div>
                                    <InputFile
                                        read
                                        filename={_ProductPicture[input.index]?.file?.name}
                                        placeholder={t("PlusAdd")}
                                        onFileSelect={file => onPictureSelect(file, input)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    {CanHideOverlay && (
                        <div style={{ display: "grid", justifyContent: "center", paddingTop: "20px" }}>
                            <InputCheckbox
                                checked={!!ProductPictureHideOverlayGradient}
                                onChange={(value, checked) => set({ ProductPictureHideOverlayGradient: checked })}
                                disabled={!ProductPictureExists}
                            >
                                {t("WizardWStep4HideOverlayGradient")}
                            </InputCheckbox>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
